import React, { useRef, useState } from "react";
import axios from "axios";
import { Mic, X, Loader2, Plus, Minus, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceOrderButton() {
  const { addItem } = useCart();
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // idle | listening | processing | results | error
  const [transcript, setTranscript] = useState("");
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const recRef = useRef(null);

  if (!user) return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;

  const start = () => {
    if (!SR) {
      setErr("Voice input isn't supported on this browser. Try Chrome on Android/desktop.");
      setState("error");
      return;
    }
    setErr("");
    setTranscript("");
    const rec = new SR();
    recRef.current = rec;
    rec.lang = "en-IN";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join(" ");
      setTranscript(t);
    };
    rec.onerror = () => {
      setErr("Couldn't hear you — please allow microphone access and try again.");
      setState("error");
    };
    rec.onend = () => {
      setState((cur) => {
        if (cur !== "listening") return cur;
        setTranscript((t) => {
          if (t.trim()) parse(t);
          else {
            setErr("Didn't catch that. Try: “2 coil Anchor wire 1.5 sq mm red FR 90 meter”");
            setState("error");
          }
          return t;
        });
        return cur === "listening" ? "listening" : cur;
      });
    };
    setState("listening");
    rec.start();
  };

  const parse = async (text) => {
    setState("processing");
    try {
      const res = await axios.post(`${API}/voice/order`, { text });
      const found = res.data.items || [];
      if (!found.length) {
        setErr("No matching products found. Try mentioning brand + product, e.g. “Havells MCB 32 amp C curve”.");
        setState("error");
        return;
      }
      setItems(found);
      setState("results");
    } catch {
      setErr("Could not process your order. Please try again.");
      setState("error");
    }
  };

  const stopListening = () => {
    recRef.current?.stop();
    if (transcript.trim()) parse(transcript);
    else setState("idle");
  };

  const setQty = (idx, delta) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, qty: Math.max(1, it.qty + delta) } : it)));

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const addAll = () => {
    for (const it of items) {
      addItem(
        {
          id: it.variant_id ? `${it.product_id}::${it.variant_id}` : it.product_id,
          name: it.name,
          price: it.price,
          mrp: it.mrp,
          image: it.image,
          brand: it.brand,
          hsn_code: it.hsn_code,
          variantInfo: it.variant_id ? { variant_id: it.variant_id, selections: it.selections } : null,
        },
        it.qty
      );
    }
    setState("idle");
    setItems([]);
  };

  const close = () => {
    recRef.current?.stop();
    setState("idle");
    setItems([]);
    setErr("");
  };

  return (
    <>
      <button
        data-testid="voice-order-btn"
        onClick={() => (state === "idle" ? start() : close())}
        title="Order by voice"
        className={`fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full shadow-lg grid place-items-center text-white transition-all ${
          state === "listening" ? "bg-[#EF4444] animate-pulse" : "bg-[#0056D2] hover:bg-[#0043A6]"
        }`}
      >
        <Mic size={20} />
      </button>

      {state !== "idle" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={close}>
          <div
            data-testid="voice-order-panel"
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-[#0056D2] text-white">
              <span className="font-extrabold text-sm uppercase tracking-wide inline-flex items-center gap-2">
                <Mic size={15} /> Voice Order
              </span>
              <button onClick={close} data-testid="voice-order-close" className="p-1 hover:bg-white/20 rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              {state === "listening" && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#EF4444]/10 grid place-items-center animate-pulse">
                    <Mic size={26} className="text-[#EF4444]" />
                  </div>
                  <p className="mt-3 text-sm font-bold text-gray-800">Listening… speak your order</p>
                  <p className="text-xs text-gray-500 mt-1 min-h-[16px]">{transcript || "e.g. “2 coil Anchor wire 1.5 sq mm red FR 90 meter”"}</p>
                  <button
                    data-testid="voice-order-stop"
                    onClick={stopListening}
                    className="mt-4 h-10 px-6 rounded-full bg-gray-900 text-white text-sm font-bold"
                  >
                    Done Speaking
                  </button>
                </div>
              )}
              {state === "processing" && (
                <div className="text-center py-8">
                  <Loader2 size={28} className="animate-spin text-[#0056D2] mx-auto" />
                  <p className="mt-3 text-sm font-bold text-gray-700">Finding your products…</p>
                  <p className="text-xs text-gray-500 mt-1">“{transcript}”</p>
                </div>
              )}
              {state === "error" && (
                <div className="py-4 text-center">
                  <p className="text-sm font-semibold text-[#EF4444]">{err}</p>
                  <button onClick={start} className="mt-4 h-10 px-6 rounded-full bg-[#0056D2] text-white text-sm font-bold">
                    Try Again
                  </button>
                </div>
              )}
              {state === "results" && (
                <>
                  <p className="text-xs text-gray-500 mb-2">Heard: “{transcript}”</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.map((it, idx) => (
                      <div key={idx} data-testid={`voice-item-${idx}`} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate">{it.name}</div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {Object.entries(it.selections || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || it.brand}
                          </div>
                          <div className="text-xs font-extrabold text-[#0056D2]">₹{it.price.toLocaleString("en-IN")}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setQty(idx, -1)} className="w-6 h-6 rounded bg-white border grid place-items-center"><Minus size={11} /></button>
                          <span className="w-6 text-center text-sm font-bold">{it.qty}</span>
                          <button onClick={() => setQty(idx, 1)} className="w-6 h-6 rounded bg-white border grid place-items-center"><Plus size={11} /></button>
                        </div>
                        <button onClick={() => removeItem(idx)} className="p-1 text-gray-400 hover:text-[#EF4444]"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="voice-add-all-btn"
                    onClick={addAll}
                    className="mt-3 w-full h-11 rounded-lg bg-[#FF5722] hover:bg-[#E64A19] text-white font-extrabold text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2"
                  >
                    <ShoppingCart size={16} /> Add {items.reduce((s, i) => s + i.qty, 0)} item(s) to cart
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
